import {
  Box,
  FormControlLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  type SxProps,
  type Theme,
} from "@mui/material";
import {
  MAX_BODY_LENGTH,
  MAX_LINK_LABEL_LENGTH,
  MAX_LINK_TARGET_LENGTH,
  MAX_TITLE_LENGTH,
} from "shared/organization-messages";
import {
  OrganizationMessageLinkType,
  OrganizationMessageSeverity,
} from "../../../generated/graphql";
import {
  DEFAULT_EXPIRY_LABEL,
  type ExpiryMode,
  LINK_TARGET_HELP,
  type MessageFormState,
  NO_LINK,
  SEVERITY_DISPLAY,
} from "../message-content";
import { SEVERITY_ICON } from "./message-chips";

const LINK_TYPE_LABEL: Record<OrganizationMessageLinkType, string> = {
  [OrganizationMessageLinkType.Documentation]: "Documentation article",
  [OrganizationMessageLinkType.Internal]: "Page in the app",
  [OrganizationMessageLinkType.External]: "Website",
};

const styles = {
  severity: {
    flexDirection: "column",
    alignItems: "flex-start",
    textTransform: "none",
    textAlign: "left",
    gap: 0.25,
  },
  severityLabel: { display: "flex", alignItems: "center", gap: 0.5 },
  sectionLabel: { fontWeight: 600 },
} satisfies Record<string, SxProps<Theme>>;

type MessageFormFieldsProps = {
  readonly form: MessageFormState;
  readonly onChange: (form: MessageFormState) => void;
};

/** Severity, text, link and expiry: the content shared by every form. */
const MessageFormFields = ({ form, onChange }: MessageFormFieldsProps) => {
  const set = <K extends keyof MessageFormState>(
    key: K,
    value: MessageFormState[K],
  ) => {
    onChange({ ...form, [key]: value });
  };

  return (
    <Stack spacing={2.5}>
      <Box>
        <Typography gutterBottom variant="body2" sx={styles.sectionLabel}>
          How urgent is it?
        </Typography>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          aria-label="Severity"
          value={form.severity}
          onChange={(_event, value: OrganizationMessageSeverity | null) => {
            if (value !== null) {
              set("severity", value);
            }
          }}
        >
          {Object.values(OrganizationMessageSeverity).map((severity) => {
            const Icon = SEVERITY_ICON[severity];
            const display = SEVERITY_DISPLAY[severity];
            return (
              <ToggleButton
                key={severity}
                value={severity}
                color={display.color}
                sx={styles.severity}
              >
                <Box sx={styles.severityLabel}>
                  <Icon fontSize="small" color={display.color} />
                  <Typography variant="body2" fontWeight={600}>
                    {display.label}
                  </Typography>
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {display.reach}
                </Typography>
              </ToggleButton>
            );
          })}
        </ToggleButtonGroup>
      </Box>
      <TextField
        multiline
        minRows={2}
        label="Message"
        placeholder="EDI messages are not working currently"
        value={form.body}
        inputProps={{ maxLength: MAX_BODY_LENGTH }}
        helperText={`Plain text. ${form.body.length}/${MAX_BODY_LENGTH}`}
        onChange={(event) => {
          set("body", event.target.value);
        }}
      />
      <TextField
        label="Title (optional)"
        value={form.title}
        inputProps={{ maxLength: MAX_TITLE_LENGTH }}
        helperText="Most messages do not need one. Shown in bold before the message."
        onChange={(event) => {
          set("title", event.target.value);
        }}
      />
      <TextField
        select
        label="Link (optional)"
        value={form.linkType}
        onChange={(event) => {
          set("linkType", event.target.value as MessageFormState["linkType"]);
        }}
      >
        <MenuItem value={NO_LINK}>No link</MenuItem>
        {Object.values(OrganizationMessageLinkType).map((type) => (
          <MenuItem key={type} value={type}>
            {LINK_TYPE_LABEL[type]}
          </MenuItem>
        ))}
      </TextField>
      {form.linkType !== NO_LINK && (
        <Stack direction="row" spacing={2}>
          <TextField
            fullWidth
            label="Link target"
            value={form.linkTarget}
            inputProps={{ maxLength: MAX_LINK_TARGET_LENGTH }}
            helperText={LINK_TARGET_HELP[form.linkType]}
            onChange={(event) => {
              set("linkTarget", event.target.value);
            }}
          />
          <TextField
            label="Button text"
            value={form.linkLabel}
            inputProps={{ maxLength: MAX_LINK_LABEL_LENGTH }}
            onChange={(event) => {
              set("linkLabel", event.target.value);
            }}
          />
        </Stack>
      )}
      <Box>
        <Typography variant="body2" sx={styles.sectionLabel}>
          Stops showing
        </Typography>
        <RadioGroup
          row
          value={form.expiryMode}
          onChange={(event) => {
            set("expiryMode", event.target.value as ExpiryMode);
          }}
        >
          <FormControlLabel
            value="DEFAULT"
            control={<Radio size="small" />}
            label={`Default (${DEFAULT_EXPIRY_LABEL[form.severity]})`}
          />
          <FormControlLabel
            value="AT"
            control={<Radio size="small" />}
            label="At a time"
          />
          <FormControlLabel
            value="NEVER"
            control={<Radio size="small" />}
            label="Never"
          />
        </RadioGroup>
        {form.expiryMode === "AT" && (
          <TextField
            type="datetime-local"
            label="Expires at"
            value={form.expiresAt}
            InputLabelProps={{ shrink: true }}
            helperText={`Your time zone: ${Intl.DateTimeFormat().resolvedOptions().timeZone}`}
            onChange={(event) => {
              set("expiresAt", event.target.value);
            }}
          />
        )}
      </Box>
    </Stack>
  );
};

export default MessageFormFields;
